// useEmailVerification.ts
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  resetPasswordEmailStatus,
  sendVerification,
  verifyOTP,
} from '@assetron/common/api';
import { ResponseStatusCode } from '@assetron/common/types/responseStatus';
import {
  EMPTY_STRING,
  RESEND_DISABLED_TIMEOUT,
  PASSWORD_RESET_PURPOSE,
  API_ERROR_CODES,
} from '@assetron/common/constants/constant';
import {
  RESET_PASSWORD_OTP,
  SIGN_UP_OTP,
} from '@assetron/web-common/constants/LocalStorageConstants';
import useTimeouts from '@assetron/common/hooks/useTimeouts';
import validateSixDigitNumber from '@assetron/common/utils/validations/validateSixDigitNumber';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useLocalization } from '@assetron/common/hooks/useLocalization';
import { notifyToaster } from '@assetron/web-common/components/Toaster/ToasterService';
import apiErrorHandler from '@assetron/common/utils/apiErrorHandler';
import { GA_EVENTS } from '@assetron/common/constants/googleAnalyticsEvents';
import { trackGaEvent } from '@assetron/common/utils/googleAnalytics';
import { billingFlowLog } from '@assetron/web-common/utils/billingFlowDiagnostics';

const useEmailVerification = (
  userEmail: string,
  purpose: string,
  onSuccess: () => void,
) => {
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [otp, setOtp] = useState(EMPTY_STRING);
  const [isInvalidOTP, setIsInvalidOTP] = useState(false);
  const { timer } = useTimeouts();
  const [, saveResetPasswordOtp] = useLocalStorage(
    RESET_PASSWORD_OTP,
    EMPTY_STRING,
  );
  const [, saveSignUpOtp] = useLocalStorage(SIGN_UP_OTP, EMPTY_STRING);
  const { t } = useLocalization();

  const handleOTPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (validateSixDigitNumber(value)) {
      setIsInvalidOTP(false);
      setOtp(value);
    }
  };

  const handleOtpSaving = (verificationPurpose: string, code: string) => {
    if (verificationPurpose === PASSWORD_RESET_PURPOSE) {
      saveResetPasswordOtp(code);
    } else {
      saveSignUpOtp(code);
    }
  };

  const verifyOTPMutation = useMutation({
    mutationFn: verifyOTP,
    onSuccess: (response) => {
      if (
        response.status === ResponseStatusCode.OK ||
        response.status === ResponseStatusCode.Accepted
      ) {
        billingFlowLog('SignupOtp', 'otp_verified', {
          purpose,
          email: userEmail,
          status: response.status,
        });
        trackGaEvent(GA_EVENTS.AUTH_OTP_SUCCESS, {
          purpose,
        });
        setIsInvalidOTP(false);
        handleOtpSaving(purpose, response.data.code);
        onSuccess();
      }
    },
    onError: (error) => {
      billingFlowLog('SignupOtp', 'otp_verify_failed', {
        purpose,
        email: userEmail,
        status: error?.response?.status,
      });
      trackGaEvent(GA_EVENTS.AUTH_OTP_FAILURE, {
        purpose,
        status_code: error?.response?.status,
      });
      const { response } = error;
      if (
        response.status === ResponseStatusCode.BadRequest ||
        response.status === ResponseStatusCode.Gone
      ) {
        setIsInvalidOTP(true);
      }
    },
  });

  const onContinueClick = async () => {
    billingFlowLog('SignupOtp', 'otp_submit', {
      purpose,
      email: userEmail,
      otpLength: otp.length,
    });
    trackGaEvent(GA_EVENTS.AUTH_OTP_SUBMIT, {
      purpose,
    });
    verifyOTPMutation.mutate({
      Email: userEmail,
      OTPCode: otp,
      Purpose: purpose,
    });
  };

  const sendVerificationMutation = useMutation({
    mutationFn: sendVerification,
    onSuccess: () => {
      trackGaEvent(GA_EVENTS.AUTH_OTP_RESEND_SUCCESS, {
        purpose,
      });
      // Standard OTP UX: resend starts a fresh verification attempt.
      setOtp(EMPTY_STRING);
      setIsInvalidOTP(false);
      notifyToaster({
        type: 'success',
        message: t('successsnackbar.emailSentSuccessfully'),
      });
      setIsResendDisabled(true);
      timer(() => setIsResendDisabled(false), RESEND_DISABLED_TIMEOUT);
    },
    onError: (error) => {
      trackGaEvent(GA_EVENTS.AUTH_OTP_RESEND_FAILURE, {
        purpose,
        status_code: error?.response?.status,
      });
      apiErrorHandler(
        error,
        {
          [API_ERROR_CODES.BAD_REQUEST]: t('authErrors.invalidEmailFormat'),
          [API_ERROR_CODES.NOT_FOUND]: t('authErrors.noUserFound'),
        },
        t('unableToSendEmail'),
      );
      setIsResendDisabled(false);
    },
  });

  const resetPasswordEmailStatusMutation = useMutation({
    mutationFn: resetPasswordEmailStatus,
    onSuccess: (response) => {
      const statusCode = response.status;
      if (
        statusCode === ResponseStatusCode.OK ||
        statusCode === ResponseStatusCode.Accepted
      ) {
        trackGaEvent(GA_EVENTS.AUTH_OTP_RESEND_SUCCESS, {
          purpose,
        });
        // Standard OTP UX: resend starts a fresh verification attempt.
        setOtp(EMPTY_STRING);
        setIsInvalidOTP(false);
        setIsResendDisabled(true);
        notifyToaster({
          type: 'success',
          message: t('successsnackbar.emailSentSuccessfully'),
        });
        timer(() => setIsResendDisabled(false), RESEND_DISABLED_TIMEOUT);
      } else {
        trackGaEvent(GA_EVENTS.AUTH_OTP_RESEND_FAILURE, {
          purpose,
          status_code: statusCode,
        });
        setIsResendDisabled(false);
        notifyToaster({ type: 'error', message: t('unableToSendEmail') });
      }
    },
    onError: (error) => {
      trackGaEvent(GA_EVENTS.AUTH_OTP_RESEND_FAILURE, {
        purpose,
        status_code: error?.response?.status,
      });
      apiErrorHandler(
        error,
        {
          [API_ERROR_CODES.BAD_REQUEST]: t('authErrors.invalidEmailFormat'),
          [API_ERROR_CODES.NOT_FOUND]: t('authErrors.noUserFound'),
        },
        t('unableToSendEmail'),
      );
    },
  });

  const onResendClick = () => {
    trackGaEvent(GA_EVENTS.AUTH_OTP_RESEND_CLICK, {
      purpose,
    });
    // Give immediate visual reset before async resend completes.
    setOtp(EMPTY_STRING);
    setIsInvalidOTP(false);

    if (purpose === PASSWORD_RESET_PURPOSE) {
      resetPasswordEmailStatusMutation.mutate({ email: userEmail });
    } else {
      sendVerificationMutation.mutate({ email: userEmail });
    }
  };

  return {
    isResendDisabled,
    otp,
    isInvalidOTP,
    handleOTPChange,
    onContinueClick,
    onResendClick,
    isLoading: verifyOTPMutation.isLoading,
  };
};

export default useEmailVerification;
